export const translations = {
  en: {
    nav: {
      byProcessor: 'By Processor',
      macbookPro: 'MacBook Pro',
      macbookAir: 'MacBook Air',
      macMini: 'Mac mini & Studio',
      iMac: 'Mac Pro & iMac',
      about: 'About'
    },
    common: {
      loading: 'Loading...',
      error: 'An error occurred',
      basedOn: 'Based on Geekbench 6 Scores',
      performanceComparison: 'Performance Comparison',
      noData: 'No data available'
    },
    titles: {
      processors: 'Mac Processor Performance Rankings',
      macbookPro: 'MacBook Pro Performance Rankings',
      macbookAir: 'MacBook Air Performance Rankings',
      macMini: 'Mac mini & Studio Performance Rankings',
      iMac: 'Mac Pro & iMac Performance Rankings',
      about: 'About Mac Processor Rankings | Website Introduction'
    },
    charts: {
      singleCore: 'Single-Core Performance (Baseline: Apple M3)',
      multiCore: 'Multi-Core Performance (Baseline: Apple M3)',
      macMiniSingleCore: 'Single-Core Performance (Baseline: Apple M2)',
      macMiniMultiCore: 'Multi-Core Performance (Baseline: Apple M2)'
    },
    about: {
      title: 'About Us',
      intro: {
        title: 'Website Introduction',
        content: 'Established in 2024, Mac Processor Rankings is dedicated to providing the most up-to-date and comprehensive performance data comparison for Apple Silicon processors. From M1 to the latest M4 series, we continuously track and update the performance of each processor to help users make informed purchasing decisions.'
      },
      features: {
        title: 'Key Features',
        processorRankings: {
          title: 'Comprehensive Processor Rankings',
          items: [
            'Complete comparison of M1/M2/M3/M4 series processors',
            'All core configuration versions included',
            'Comparison data with Intel processors'
          ]
        },
        productComparison: {
          title: 'Product Series Performance Comparison',
          items: [
            'MacBook Pro series performance rankings',
            'MacBook Air performance comparison',
            'Mac mini and Mac Studio performance comparison',
            'iMac and Mac Pro performance data'
          ]
        }
      },
      data: {
        title: 'Data Information',
        intro: 'All performance data is based on Geekbench 6 benchmark results, one of the most authoritative cross-platform performance testing tools available. We choose Geekbench 6 for the following reasons:',
        testMethod: {
          title: 'Testing Methods',
          items: [
            'Scientific testing methodology',
            'Stable and reliable results',
            'Regularly updated test items'
          ]
        },
        testCoverage: {
          title: 'Test Coverage',
          items: [
            'Single-core performance testing',
            'Multi-core performance testing',
            'Real-world scenario simulation'
          ]
        }
      },
      updates: {
        title: 'Updates & Maintenance',
        dataUpdates: {
          title: 'Data Updates',
          items: [
            'Updates within 24 hours of new product releases',
            'Weekly data accuracy checks'
          ]
        },
        siteOptimization: {
          title: 'Site Optimization',
          items: [
            'Continuous user experience improvements',
            'Regular feature updates'
          ]
        }
      },
      contact: {
        title: 'Contact & Feedback',
        content: 'We value feedback and suggestions from every user. If you have any questions, suggestions, or find any data errors, please feel free to contact us through:'
      }
    },
    descriptions: {
      about: 'Mac Processor Rankings provides comprehensive performance comparisons of Apple Silicon M1/M2/M3/M4 series processors.'
    }
  },
  zh: {
    nav: {
      byProcessor: '处理器',
      macbookPro: 'MacBook Pro',
      macbookAir: 'MacBook Air',
      macMini: 'Mac mini & Studio',
      iMac: 'Mac Pro & iMac',
      about: '关于'
    },
    common: {
      loading: '加载中...',
      error: '发生错误',
      basedOn: '基于 Geekbench 6 跑分数据',
      performanceComparison: '性能对比',
      noData: '暂无数据'
    },
    titles: {
      processors: 'Mac 处理器性能排名',
      macbookPro: 'MacBook Pro 性能排名',
      macbookAir: 'MacBook Air 性能排名',
      macMini: 'Mac mini & Studio 性能排名',
      iMac: 'Mac Pro & iMac 性能排名',
      about: '关于 Mac 处理器性能排名 | 网站介绍'
    },
    charts: {
      singleCore: '单核性能 (基准：Apple M3)',
      multiCore: '多核性能 (基准：Apple M3)',
      macMiniSingleCore: '单核性能 (基准：Apple M2)',
      macMiniMultiCore: '多核性能 (基准：Apple M2)'
    },
    about: {
      title: '关于本站',
      intro: {
        title: '网站简介',
        content: 'Mac 处理器性能排名网站创建于 2024 年，致力于为用户提供最新、最全面的 Apple Silicon 处理器性能数据对比。从 M1 到最新的 M4 系列，我们持续追踪和更新各款处理器的性能表现，帮助用户做出明智的购买决策。'
      },
      features: {
        title: '主要功能',
        processorRankings: {
          title: '全面的处理器性能排名',
          items: [
            'M1/M2/M3/M4 系列处理器完整对比',
            '包含所有核心配置版本',
            '与 Intel 处理器的对比数据'
          ]
        },
        productComparison: {
          title: '产品系列性能对比',
          items: [
            'MacBook Pro 全系列性能排名',
            'MacBook Air 性能对比',
            'Mac mini 与 Mac Studio 性能对比',
            'iMac 与 Mac Pro 性能数据'
          ]
        }
      },
      data: {
        title: '数据说明',
        intro: '所有性能数据均基于 Geekbench 6 跑分结果，这是目前最权威的跨平台性能测试工具之一。我们选择 Geekbench 6 的原因：',
        testMethod: {
          title: '测试方法',
          items: [
            '科学的测试方法',
            '结果稳定可靠',
            '定期更新测试项目'
          ]
        },
        testCoverage: {
          title: '测试覆盖',
          items: [
            '单核心性能测试',
            '多核心性能测试',
            '真实场景性能模拟'
          ]
        }
      },
      updates: {
        title: '更新与维护',
        dataUpdates: {
          title: '数据更新',
          items: [
            '新品发布 24 小时内更新',
            '每周检查数据准确性'
          ]
        },
        siteOptimization: {
          title: '网站优化',
          items: [
            '持续改进用户体验',
            '定期功能更新'
          ]
        }
      },
      contact: {
        title: '联系与反馈',
        content: '我们重视每一位用户的反馈和建议。如果您有任何问题、建议或发现数据有误，欢迎通过以下方式联系我们：'
      }
    },
    descriptions: {
      about: 'Mac 处理器性能排名网站提供最新、最全面的 Apple Silicon M1/M2/M3/M4 系列处理器性能数据对比。'
    }
  }
};